import {Breadcrumbs} from 'components/customer/Product/Breadcrumbs/Breadcrumbs';
import {selectSearchText} from 'components/customer/Product/store/productSlice';
import {useProductContext} from 'contexts';
import {SHOW_ALL_BUTTON} from 'hooks/Product/Product';
import {FAVOURITE_CATEGORY_NAME} from 'hooks/QuickProduct/ProductSearch';
import React, {useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {mobilePortrait} from 'shared/Breakpoints';
import {CategoryInterface} from 'shared/components/Product/Category';
import {useAppSelector} from 'store/customer';
import styled, {css} from 'styled-components';

interface TitleAndBreadcrumbsProps {
    categories: CategoryInterface[];
    itemsCount: number;
}

export const TitleAndBreadcrumbs = ({
    categories,
    itemsCount,
}: TitleAndBreadcrumbsProps) => {
    const {isMiniBrowser, breadcrumbs} = useProductContext();
    const search = useAppSelector(selectSearchText);
    const [searchParams] = useSearchParams();

    const categoryCount = useMemo(() => {
        if (searchParams.has('favourites')) return '';

        if (categories) {
            const categoriesCount = Object.keys(categories).length;

            if (categoriesCount > 0) {
                let categoryCount = `${categoriesCount} categories - `;

                if (itemsCount == 0) {
                    const productCount = categories
                        .filter(
                            (category) =>
                                category.name != FAVOURITE_CATEGORY_NAME &&
                                category.name != SHOW_ALL_BUTTON
                        )
                        .reduce((amount, category) => {
                            return (category.productCount ?? 0) + amount;
                        }, 0);

                    categoryCount += `${productCount} products in total`;
                }

                return categoryCount;
            }
        }

        return '';
    }, [categories, itemsCount, searchParams]);

    const title = useMemo(() => {
        if (breadcrumbs && breadcrumbs.length > 0) {
            const categoryName = breadcrumbs[breadcrumbs.length - 1];

            if (categoryName) {
                return categoryName.key;
            }
        }

        if (search) {
            return 'Products';
        }

        return 'Product Categories';
    }, [breadcrumbs, search]);

    return (
        <>
            <CategoriesTitle $isMiniBrowser={isMiniBrowser ?? false}>
                <Main>{title}</Main>
                <Counts>
                    {search ? null : categoryCount}{' '}
                    {itemsCount != undefined && itemsCount > 0
                        ? `${itemsCount} ${itemsCount == 1 ? 'product' : 'products in total'}`
                        : null}
                </Counts>
            </CategoriesTitle>
            <div className="search-info">
                <Breadcrumbs />
            </div>
        </>
    );
};

const SmallBrowser = css`
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
`;

const CategoriesTitle = styled.div<{$isMiniBrowser: boolean}>`
    margin-top: ${({$isMiniBrowser}) => ($isMiniBrowser ? '4px' : '15px')};
    display: flex;

    ${({$isMiniBrowser}) => {
        if ($isMiniBrowser) {
            return SmallBrowser;
        } else {
            return css`
                justify-content: flex-start;
                align-items: center;
                gap: 15px;
            `;
        }
    }}

    ${mobilePortrait`${SmallBrowser}`};
`;

const Main = styled.div`
    font-size: 1.3em;
    font-weight: 500;
    color: #005a7d;
`;

const Counts = styled.div`
    color: gray;
    justify-content: flex-start;
    align-items: center;
`;
